// ============================================================================
// Log a delivery — the ONE component that writes a delivery (PLAYBOOK §15a,
// TCSD-0294). Every screen offering "Log a delivery" renders this.
//
// Order on screen is the order it happens at the clinic: what is on the shelf
// FIRST (the count, taken before anything lands), then what you are leaving
// (pre-filled to 30 days at this clinic's rate), from which location, then the
// first-delivery changeover checks, then today's route stop if there is one.
//
// ⛔ The route stop is checked off through the AM Routes module's OWN write
// (useTodayRoute().visitStop) — there is no second copy of it here.
// ⛔ The draft resets every time the window opens and after a successful save.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../shared/auth';
import { useTodayRoute } from '../../am-routes/hooks/use-am-routes-data';
import { fetchLocationStock, fetchPhasingOut, logDelivery, type ClinicStockRow, type LocationStockRow, type OfficeRow } from './api';
import { onHandText, sourceChoices } from './stock-sources';
import { useStockLoad } from './use-stock-data';
import { fmtQty, roundLeave } from './format';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';
import { LoggingAs } from './logging-as';

interface Draft {
  counts: Record<string, string>;
  leaves: Record<string, string>;
  officeId: string;
  sds: boolean;
  oldPicked: boolean;
  bottles: string;
  checkStop: boolean;
  notes: string;
}

export function LogDeliveryWindow({
  open,
  onClose,
  rows,
  offices,
}: {
  open: boolean;
  onClose: () => void;
  /** The clinic's rows, one per standard chemical. */
  rows: ClinicStockRow[];
  offices: OfficeRow[];
}) {
  const { employee } = useAuth();
  const me = employee?.employee_id ?? null;
  const first = rows[0];
  const facilityId = first?.facility_id ?? '';
  const isFirstDelivery = rows.every((r) => (r.deliveries ?? 0) === 0);

  // Today's route for the person logging — the stop is theirs to tick.
  const route = useTodayRoute(open ? me : null, me);
  const stop = useMemo(
    () => route.stops.find((s) => s.facilityId === facilityId && s.status === 'Planned') ?? null,
    [route.stops, facilityId],
  );

  const [phasing, setPhasing] = useState<Array<{ fc_id: string; name: string }>>([]);
  // Where it can come from: the stock locations in this clinic's region, with what each holds (TCSD-0388).
  const stockQ = useStockLoad(() => (open ? fetchLocationStock() : Promise.resolve([])), [open], [] as LocationStockRow[]);
  const choices = useMemo(
    () => sourceChoices(offices, first?.stock_office_id, stockQ.data),
    [offices, first?.stock_office_id, stockQ.data],
  );
  const [draft, setDraft] = useState<Draft>(() => blank(rows));

  function blank(rs: ClinicStockRow[]): Draft {
    const leaves: Record<string, string> = {};
    for (const r of rs) {
      const target = (r.fill_to_days ?? 30) * (r.rate_day ?? 0) - (r.shelf_now ?? 0);
      leaves[r.supply_id ?? ''] = String(roundLeave(target, r.unit_label));
    }
    return {
      counts: {},
      leaves,
      officeId: rs[0]?.stock_office_id ?? '',
      sds: false,
      oldPicked: false,
      bottles: '',
      checkStop: true,
      notes: '',
    };
  }

  // ⛔ Reset on open, so a cancelled or saved draft never comes back.
  useEffect(() => {
    if (!open) return;
    setDraft(blank(rows));
    if (facilityId) {
      fetchPhasingOut(facilityId).then(setPhasing).catch(() => setPhasing([]));
    }
    // rows identity changes on every refetch; the window must not reset under
    // somebody's thumbs, so reset on open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facilityId]);

  // Pre-fill "leaving" from the count once one is typed: fill to 30 days.
  function setCount(sid: string, v: string) {
    setDraft((d) => {
      const r = rows.find((x) => x.supply_id === sid);
      const counted = parseQty(v);
      const leaves = { ...d.leaves };
      if (r && counted != null) {
        leaves[sid] = String(roundLeave((r.fill_to_days ?? 30) * (r.rate_day ?? 0) - counted, r.unit_label));
      }
      return { ...d, counts: { ...d.counts, [sid]: v }, leaves };
    });
  }

  const office = choices.find((o) => o.office_id === draft.officeId);
  const leaving = rows
    .map((r) => ({ supply_id: r.supply_id ?? '', qty: parseQty(draft.leaves[r.supply_id ?? ''] ?? '') ?? 0 }))
    .filter((l) => l.qty > 0);
  const canSave = !!draft.officeId && !!office && leaving.length > 0;
  // Only the products this delivery would take below zero are named in the warning (TCSD-0385).
  const short = office ? rows.filter((r) => leaving.some((l) => l.supply_id === r.supply_id && l.qty > (office.onHand[l.supply_id] ?? 0))) : [];

  async function save() {
    const counts = rows
      .map((r) => ({ supply_id: r.supply_id ?? '', qty: parseQty(draft.counts[r.supply_id ?? ''] ?? '') }))
      .filter((c): c is { supply_id: string; qty: number } => c.qty != null);
    await logDelivery({
      facilityId,
      officeId: draft.officeId,
      counts,
      leaves: leaving,
      sdsPosted: isFirstDelivery ? draft.sds : null,
      oldPickedUp: isFirstDelivery && phasing.length > 0 ? draft.oldPicked : null,
      bottlesLabeled: isFirstDelivery ? parseQty(draft.bottles) : null,
      dayStopId: stop && draft.checkStop ? stop.dayStopId : null,
      notes: draft.notes || null,
    });
    // The delivery is saved. The route stop is a separate, existing write.
    if (stop && draft.checkStop) {
      try {
        await route.visitStop({ dayStopId: stop.dayStopId, assignmentId: stop.assignmentId });
      } catch {
        setDraft(blank(rows));
        onClose();
        window.alert('The delivery is saved. The route stop could not be checked off — tick it on Today’s route.');
        return;
      }
    }
    setDraft(blank(rows));
    onClose();
  }

  if (!first) return null;

  return (
    <StockWindow
      open={open}
      title="Log a delivery"
      subtitle={first.display_name}
      canSave={canSave}
      saveLabel="Save delivery"
      onCancel={onClose}
      onSave={save}
    >
      <LoggingAs amEid={first.account_manager_eid} amName={first.account_manager_name} />

      <section>
        <h4 className="text-sm font-semibold text-tcs-text">What’s on the shelf right now?</h4>
        <p className="text-xs text-tcs-text-muted">
          Count before you unload. Leave it empty if you did not count — the shelf is then worked out from the log.
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {rows.map((r) => (
            <QtyField
              key={r.supply_id}
              label={r.unit_label === 'tabs' ? 'Stearns, tabs' : 'Micro-Chem, gallons'}
              value={draft.counts[r.supply_id ?? ''] ?? ''}
              onChange={(v) => setCount(r.supply_id ?? '', v)}
              step={r.unit_label === 'tabs' ? 1 : 0.1}
            />
          ))}
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-tcs-text">What are you leaving?</h4>
        <p className="text-xs text-tcs-text-muted">Pre-filled for {rows[0].fill_to_days ?? 30} days at this clinic’s rate. Change it freely.</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {rows.map((r) => (
            <QtyField
              key={r.supply_id}
              label={r.unit_label === 'tabs' ? 'Stearns, tabs' : 'Micro-Chem, gallons'}
              value={draft.leaves[r.supply_id ?? ''] ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, leaves: { ...d.leaves, [r.supply_id ?? '']: v } }))}
              step={r.unit_label === 'tabs' ? 5 : 0.5}
              hint={r.shelf_now != null ? `Shelf now about ${fmtQty(r.shelf_now, r.unit_label)}` : undefined}
            />
          ))}
        </div>
      </section>

      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">From</span>
        <select
          className={FIELD_CLASS}
          value={draft.officeId}
          onChange={(e) => setDraft((d) => ({ ...d, officeId: e.target.value }))}
        >
          <option value="">— pick a stock location —</option>
          {choices.map((o) => (
            <option key={o.office_id} value={o.office_id}>
              {o.office_name} — {onHandText(o, rows)} on hand
              {o.isDefault ? ' (this clinic’s)' : ''}
            </option>
          ))}
        </select>
        {office && short.length > 0 && (
          <span className="mt-1 block text-[11px] text-st-warn-fg">
            Records show {onHandText(office, short)} here — this will show a negative balance until someone counts.
          </span>
        )}
      </label>

      {isFirstDelivery && (
        <section className="rounded-lg border border-tcs-border bg-tcs-surface p-3">
          <h4 className="text-sm font-semibold text-tcs-text">First delivery — the changeover</h4>
          <label className="mt-2 flex items-center gap-2 text-sm text-tcs-text">
            <input type="checkbox" checked={draft.sds} onChange={(e) => setDraft((d) => ({ ...d, sds: e.target.checked }))} />
            SDS sheets posted for both products
          </label>
          {phasing.length > 0 && (
            <label className="mt-2 flex items-start gap-2 text-sm text-tcs-text">
              <input
                type="checkbox"
                className="mt-1"
                checked={draft.oldPicked}
                onChange={(e) => setDraft((d) => ({ ...d, oldPicked: e.target.checked }))}
              />
              <span>
                Old chemicals picked up ({phasing.map((p) => p.name).join(', ')})
                <span className="block text-[11px] text-tcs-text-muted">
                  Ticking this takes them off this building’s chemical list.
                </span>
              </span>
            </label>
          )}
          <label className="mt-2 block">
            <span className="mb-1 block text-xs text-tcs-text-muted">Bottles labeled (optional)</span>
            <input
              inputMode="numeric"
              className={FIELD_CLASS}
              value={draft.bottles}
              onChange={(e) => setDraft((d) => ({ ...d, bottles: e.target.value.replace(/[^0-9]/g, '') }))}
            />
          </label>
        </section>
      )}

      {stop && (
        <label className="flex items-center gap-2 text-sm text-tcs-text">
          <input
            type="checkbox"
            checked={draft.checkStop}
            onChange={(e) => setDraft((d) => ({ ...d, checkStop: e.target.checked }))}
          />
          Check off today’s route stop
        </label>
      )}

      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Anything worth saying (optional)</span>
        <input className={FIELD_CLASS} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
      </label>
    </StockWindow>
  );
}
